import React from 'react';
import { useRecommendation, useInactivityReset } from '../hooks/useRecommendation';
import { useImagePrefetch } from '../hooks/useImagePrefetch';
import QuestionCard from './QuestionCard';
import ProgressBar from './ProgressBar';
import Results from './Results';
import { useLanguage } from '../context/LanguageContext';

interface SelectorProps {
  onResetToLanguageSelection: () => void;
}

const Selector: React.FC<SelectorProps> = ({ onResetToLanguageSelection }) => {
  const { t } = useLanguage();
  const {
    currentQuestion,
    currentQuestionIndex,
    totalQuestions,
    handleAnswer,
    goToPreviousQuestion,
    resetQuiz,
    answers,
    recommendedProducts,
    productMatches,
    alternativeProducts,
    unmatchedCriteria,
    isComplete,
    hasPerfectMatch
  } = useRecommendation();

  // Prefetch likely result images as user progresses through quiz
  useImagePrefetch(recommendedProducts, { 
    maxPrefetch: 8, // Prefetch top 8 likely products
    priority: 'low' // Don't interfere with current page loading
  });

  const handleReset = () => {
    resetQuiz();
    onResetToLanguageSelection();
  };

  // Enable inactivity reset on question cards
  useInactivityReset(handleReset, !isComplete);

  return (
    <div className="w-full max-w-5xl mx-auto">
      {!isComplete ? (
        <div className="w-full">
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-felco-red mb-3 tracking-tight">{t('app.title')}</h1>
            <p className="text-gray-700 text-xl mb-6">{t('app.subtitle')}</p>
          </div>
          
          <ProgressBar 
            currentStep={currentQuestionIndex + 1} 
            totalSteps={totalQuestions} 
          />
          
          <QuestionCard 
            question={currentQuestion}
            onAnswer={handleAnswer}
            currentAnswer={answers[currentQuestion.id]}
          />
          
          <div className="mt-6 flex justify-between">
            {currentQuestionIndex > 0 && (
              <button 
                onClick={goToPreviousQuestion}
                className="px-5 py-2 border border-felco-red text-felco-red rounded-lg hover:bg-felco-red hover:bg-opacity-5 transition-colors"
              >
                {t('btn.back')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <Results 
          products={recommendedProducts}
          productMatches={productMatches}
          alternativeProducts={alternativeProducts}
          unmatchedCriteria={unmatchedCriteria}
          hasPerfectMatch={hasPerfectMatch}
          onReset={handleReset}
        />
      )}
    </div>
  );
};

export default Selector; 